import { useState } from 'react'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateDocumentChangeSets } from '@/service-library/hooks/doc-changes-sync'
import { ChangeSet } from '@/types/doc-changes'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import PaginatedResponse from '@/types/paginated-response'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'

export type RowInfo = {
  docId: string
  rowId: string
  existingValueId?: string
}

type ValueEntry = {
  manual_value: string | null
  manual_ref_id: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  manual_value_json?: Record<string, any> | null
}

type UseBulkChangeSetOptions = {
  field: ProjectGridField
  workflowId: string
  workflowStateId: string
  listQueryKey?: QueryKey
  setPendingEdits: (
    fieldId: string,
    rowIdsByDoc: Map<string, Set<string>>,
  ) => void
  overlay: OverlayState
  successMessage: (docCount: number) => string
  errorMessage: string
}

export default function useBulkChangeSet({
  field,
  workflowId,
  workflowStateId,
  listQueryKey,
  setPendingEdits,
  overlay,
  successMessage,
  errorMessage,
}: UseBulkChangeSetOptions) {
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const { createDocumentChangeSets } = useCreateDocumentChangeSets({
    onSuccess: ({ completed }, changeSets) => {
      showSuccessSnackbar(successMessage(changeSets.length))
      setIsSaving(false)
      overlay.close()

      // A completed run means the server already applied every change set we
      // sent, so the fresh list is the only thing left to fetch. A queued one
      // (202) lands later, so fall back to waiting for its notifications.
      if (completed) {
        if (listQueryKey) queryClient.refetchQueries({ queryKey: listQueryKey })
        return
      }

      const pendingEdits = new Map<string, Set<string>>()
      changeSets.forEach((cs) => {
        pendingEdits.set(
          cs.doc_id,
          new Set(
            cs.values?.flatMap((v) => (v.row_id ? [v.row_id] : [])) ?? [],
          ),
        )
      })
      setPendingEdits(field.id, pendingEdits)
    },
    onError: () => {
      showErrorSnackbar(errorMessage)
      if (listQueryKey) {
        queryClient.invalidateQueries({ queryKey: listQueryKey })
      }
      setIsSaving(false)
    },
  })

  function submit(selectedRows: Required<DocumentRow>[], value: ValueEntry) {
    setIsSaving(true)

    // Optimistic cache update — create new row references so MRT re-renders
    const selectedRowIds = new Set(selectedRows.map((row) => row.id))
    if (listQueryKey) {
      queryClient.setQueryData<InfiniteData<PaginatedResponse<DocumentRow>>>(
        listQueryKey,
        (old = { pages: [], pageParams: [] }) => ({
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: page.results.map((storedRow) =>
              selectedRowIds.has(storedRow.id) ? { ...storedRow } : storedRow,
            ),
          })),
        }),
      )
    }

    // Group selected rows by document
    const documentRowMap = new Map<string, RowInfo[]>()
    selectedRows.forEach((row) => {
      const docId = row.document_id
      const existingValue = row.document_row_values?.find(
        (v) => v.project_grid_field_id === field.id,
      )
      if (!documentRowMap.has(docId)) {
        documentRowMap.set(docId, [])
      }
      const rowsForDoc = documentRowMap.get(docId) as RowInfo[]
      rowsForDoc.push({
        docId,
        rowId: row.id,
        existingValueId: existingValue?.id as string,
      })
    })

    // Build change sets
    const changeSets: ChangeSet[] = []
    documentRowMap.forEach((rows, docId) => {
      changeSets.push({
        id: generateUuid(),
        doc_id: docId,
        workflow_id: workflowId,
        workflow_state_id: workflowStateId,
        values: rows.map((rowInfo) => ({
          ...value,
          op: rowInfo.existingValueId ? 'update' : 'new',
          value_id: rowInfo.existingValueId || generateUuid(),
          row_id: rowInfo.rowId,
          field_id: field.id,
        })),
      })
    })

    createDocumentChangeSets(changeSets)
  }

  return { submit, isSaving }
}
